import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronRight } from "lucide-react";
import {
  hasDowntimeHours,
  roundHoursForDisplay,
  shutdownListEventHours,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";
import { cn } from "@/lib/utils.js";
import { Badge } from "@/components/ui/badge.jsx";
import LoadingIndicator from "@/components/ui/loading-indicator.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import {
  getDowntimeDurationSeverityClasses,
  getOngoingDowntimeBadgeClass,
  getOngoingDowntimeDotClass,
} from "@/lib/downtime-duration-styles.js";

/** ~2/3 of max-w-md (28rem) to leave room for a future column. */
const LIST_MAX_WIDTH_CLASS = "max-w-[18.67rem]";

/**
 * After this delay, show reassurance copy for slow shutdown-event fetches.
 * Because some assets have tons of shutdown events and takes a little while to load.
 * Which is an issue that will be considered for future releases.
 */
const SLOW_LOAD_DELAY_MS = 4000;

const LOADING_ROW_SKELETON_COUNT = 5;

/**
 * Formats reporting date/time for the shutdown list (12-hour clock).
 * @param {string} iso
 * @returns {string} e.g. "May 3, 07:16 AM" (`hh:mm` with AM/PM)
 */
function formatReportingDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/**
 * Formats the downtime duration to a summary that is displayed in the shutdown list.
 * @param {number} dtHours
 * @returns {string} e.g. "4h down"
 */
function formatDownHoursLabel(dtHours) {
  const h = Number(dtHours) || 0;
  return `${roundHoursForDisplay(h)}h down`;
}

/**
 * Primary line: alarm text from the API, or an em dash when empty.
 * @param {{ RelatedAlarm?: string }} event
 * @returns {string}
 */
function rowTitle(event) {
  const t = (event.RelatedAlarm || "").trim();
  return t || "—";
}

/**
 * @param {{
 *   eventCount: number|null,
 *   isLoading: boolean,
 *   showResultsLimitNote: boolean,
 *   resultsLimit: number,
 * }} props
 */
function ShutdownListCaption({
  eventCount,
  isLoading,
  showResultsLimitNote,
  resultsLimit,
}) {
  const countLabel = eventCount == null ? null : ` (${eventCount})`;
  const subtitle = isLoading
    ? "Loading shutdown events…"
    : "Click to investigate";

  return (
    <div className='border-b border-border px-3 py-3 sm:px-4'>
      <div className='flex items-start gap-2'>
        <AlertTriangle
          className='mt-0.5 h-5 w-5 shrink-0 text-orange-500 dark:text-orange-400'
          aria-hidden
        />
        <div className='min-w-0'>
          <h2 className='text-base font-semibold leading-tight text-foreground'>
            Shutdown Events
            {countLabel}
          </h2>
          <p className='mt-1 text-xs text-muted-foreground'>{subtitle}</p>
          {showResultsLimitNote ? (
            <p className='mt-1 text-xs text-amber-800 dark:text-amber-300'>
              This unit has a lot of shutdowns. Only the most recent{" "}
              {resultsLimit} are shown.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Loading placeholder: spinner, optional slow-load reassurance, and list-row skeletons.
 * @returns {JSX.Element}
 */
function ShutdownListLoading() {
  const [showSlowLoadMessage, setShowSlowLoadMessage] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setShowSlowLoadMessage(true);
    }, SLOW_LOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className='flex min-h-0 flex-1 flex-col p-3 sm:p-4' aria-busy='true'>
      <div
        className='mb-3 flex shrink-0 flex-col items-center gap-1.5 text-center'
        role='status'
        aria-live='polite'>
        <LoadingIndicator size='lg' className='justify-center text-muted-foreground' />
        {showSlowLoadMessage ? (
          <p className='text-xs text-muted-foreground'>
            Still loading — this may take a moment for assets with long history
          </p>
        ) : null}
      </div>
      <ul className='min-h-0 flex-1 divide-y divide-border' aria-hidden>
        {Array.from({ length: LOADING_ROW_SKELETON_COUNT }, (_, index) => (
          <li
            key={`loading-row-${index}`}
            className='flex items-stretch gap-2.5 py-2.5'>
            <div className='flex shrink-0 items-start pt-0.5'>
              <LoadingSkeleton className='h-2 w-2 rounded-full' />
            </div>
            <div className='min-w-0 flex-1 space-y-1.5'>
              <LoadingSkeleton
                className={cn(
                  "h-4 rounded",
                  index % 2 === 0 ? "w-[92%]" : "w-[78%]"
                )}
              />
              <LoadingSkeleton className='h-3 w-[55%] rounded' />
            </div>
            <div className='flex shrink-0 items-center self-center pl-1'>
              <LoadingSkeleton className='h-5 w-5 rounded' />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @typedef {Object} ShutdownSelectionPayload
 * @property {string|number} assetId - Fleet asset id (same as `fleetAssetId`)
 * @property {string} reportingDate - ISO 8601 reporting start from the clicked row
 * @property {number} uniqueId - `UniqueId` from the clicked row (for highlight / correlation)
 */

/**
 * Scrollable shutdown list for one asset. Parent loads rows via `getAllShutdownEventsForAsset` and passes them in.
 * @param {{
 *   fleetAssetId: string|number,
 *   shutdownEvents: Array<{ UniqueId: number, ReportingDate: string, RelatedAlarm?: string, DowntimeHours?: number|null }>,
 *   shutdownEventsLoading: boolean,
 *   shutdownEventsError: string|null,
 *   resultsLimit: number,
 *   selectedUniqueId?: number|null,
 *   onSelectShutdownEvent?: (payload: ShutdownSelectionPayload) => void,
 * }} props
 * @returns {JSX.Element}
 */
export default function ShutdownEventsList({
  fleetAssetId,
  shutdownEvents,
  shutdownEventsLoading,
  shutdownEventsError,
  resultsLimit,
  selectedUniqueId = null,
  onSelectShutdownEvent,
}) {
  const sortedEvents = useMemo(
    () =>
      [...shutdownEvents].sort(
        (a, b) =>
          new Date(b.ReportingDate).getTime()
          - new Date(a.ReportingDate).getTime()
      ),
    [shutdownEvents]
  );

  const eventCount = shutdownEventsLoading ? null : sortedEvents.length;
  const showResultsLimitNote =
    !shutdownEventsLoading
    && !shutdownEventsError
    && sortedEvents.length >= resultsLimit;

  // Card height ≈ viewport minus asset chrome; body flexes so the list scrolls inside, and keeps the change confined to this component.
  const shellClass = cn(
    "flex h-[calc(100dvh-14rem)] min-h-[12rem] w-full flex-col overflow-hidden rounded-lg border border-border bg-card",
    LIST_MAX_WIDTH_CLASS
  );

  return (
    <div className={shellClass} aria-busy={shutdownEventsLoading || undefined}>
      <ShutdownListCaption
        eventCount={eventCount}
        isLoading={shutdownEventsLoading}
        showResultsLimitNote={showResultsLimitNote}
        resultsLimit={resultsLimit}
      />

      <div className='flex min-h-0 flex-1 flex-col'>
        {shutdownEventsLoading ? <ShutdownListLoading /> : null}

        {shutdownEventsError ? (
          <div className='flex flex-1 flex-col px-3 py-3 sm:px-4'>
            <p className='text-sm text-red-600 dark:text-red-400'>
              {shutdownEventsError}
            </p>
          </div>
        ) : null}

        {!shutdownEventsLoading
        && !shutdownEventsError
        && sortedEvents.length === 0 ? (
          <div className='flex flex-1 flex-col px-3 py-3 sm:px-4'>
            <p className='text-sm text-muted-foreground'>No shutdown events.</p>
          </div>
        ) : null}

        {!shutdownEventsLoading
        && !shutdownEventsError
        && sortedEvents.length > 0 ? (
          <ul className='min-h-0 flex-1 divide-y divide-border overflow-y-auto'>
            {sortedEvents.map((event, index) => {
              const dtHours = shutdownListEventHours(event);
              const sev = getDowntimeDurationSeverityClasses(dtHours);
              return (
                <li
                  key={
                    event.UniqueId ? String(event.UniqueId) : `row-${index}`
                  }>
                  <button
                    type='button'
                    className={cn(
                      "flex w-full items-stretch gap-2.5 px-3 py-2.5 text-left transition-colors sm:px-4",
                      "cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60",
                      selectedUniqueId != null
                        && event.UniqueId === selectedUniqueId
                        && "bg-muted hover:bg-muted"
                    )}
                    aria-pressed={
                      selectedUniqueId != null
                      && event.UniqueId === selectedUniqueId
                    }
                    onClick={() =>
                      onSelectShutdownEvent?.({
                        assetId: fleetAssetId,
                        reportingDate: event.ReportingDate,
                        uniqueId: event.UniqueId,
                      })
                    }>
                    <div className='flex shrink-0 items-start pt-0.5'>
                      <span
                        className={cn(
                          "inline-block h-2 w-2 rounded-full",
                          sev.dot
                        )}
                        aria-hidden
                      />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='line-clamp-2 break-words text-sm font-semibold leading-snug text-foreground'>
                        {rowTitle(event)}
                      </p>
                      <div className='mt-0.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-[11px] leading-snug'>
                        <span className='shrink-0 text-muted-foreground'>
                          {formatReportingDateTime(event.ReportingDate)}
                        </span>
                        {hasDowntimeHours(event) ? (
                          <span
                            className={cn(
                              "shrink-0 tabular-nums font-medium",
                              sev.duration
                            )}>
                            {formatDownHoursLabel(dtHours)}
                          </span>
                        ) : (
                          <Badge
                            variant='status'
                            className={getOngoingDowntimeBadgeClass(
                              "shrink-0 px-2 py-0 text-[10px] font-medium leading-tight"
                            )}>
                            <span
                              className={getOngoingDowntimeDotClass(
                                "h-1.5 w-1.5"
                              )}
                              aria-hidden='true'
                            />
                            Ongoing
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className='flex shrink-0 items-center self-center pl-1'>
                      <ChevronRight
                        className='h-5 w-5 shrink-0 text-muted-foreground'
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
